import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Typography } from '@mui/material';
import Accordion from 'app/shared/components/common/Accordion';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { LoanSubtabRouteParam } from 'constants/loanApplication';
import { getTabEntityUiManageId } from 'utils/loanApplication';

import { selectIsEditingSection } from 'store/loanApplications/selectors';

import ChecklistItemsTable from './ChecklistItemsTable';
import { ChecklistsTestIds } from './Checklists.testIds';

const overrideAccordionClasses = {
  root: {
    '& .MuiAccordionSummary-root': {
      background: ({ palette }) => palette.background.paper,
    },
  },
};

const ChecklistCategory = ({
  name,
  checklistId,
  items,
  categoryId,
  calculationResult,
  classes,
  isAccordion = true,
}) => {
  const checklistCategoryUiManageId = getTabEntityUiManageId({
    tabName: LoanSubtabRouteParam.CHECKLISTS,
    id: categoryId,
  });

  const isEditMode = useSelector(selectIsEditingSection(checklistCategoryUiManageId));

  const filteredItems = useMemo(() => {
    if (!items) {
      return [];
    }

    const resultItemsMap = _.keyBy(calculationResult.items, 'checklistItemId');

    return items.filter(({ id }) => !!resultItemsMap[id]?.visibilityExpressionResult);
  }, [calculationResult.items, items]);

  if (!isAccordion) {
    return (
      <ChecklistItemsTable
        applicationId={calculationResult.applicationId}
        checklistId={checklistId}
        isEditMode={isEditMode}
        tableData={filteredItems}
        tableId={checklistCategoryUiManageId}
      />
    );
  }

  return (
    <Accordion
      bottom={
        <ChecklistItemsTable
          applicationId={calculationResult.applicationId}
          checklistId={checklistId}
          isEditMode={isEditMode}
          tableData={filteredItems}
          tableId={checklistCategoryUiManageId}
        />
      }
      className={clsx(classes.accordion, overrideAccordionClasses.root, 'border-none')}
      data-testid={ChecklistsTestIds.getChecklistCategoryId(categoryId)}
      defaultExpanded={false}
      id={categoryId}
      key={categoryId}
      top={<Typography variant="regular14">{name}</Typography>}
      unmountOnExit={!isEditMode}
      variant="outlined"
    />
  );
};

ChecklistCategory.propTypes = {
  name: PropTypes.string.isRequired,
  checklistId: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  categoryId: PropTypes.string.isRequired,
  calculationResult: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  isAccordion: PropTypes.bool,
};

export default memo(ChecklistCategory);
